/**
 * Friends-only leaderboard client.
 *
 * Submits the local user's per-game best stats to the server and fetches
 * records for any list of userIds (the leaderboard page passes friend ids
 * + the local user's id).
 *
 * Anti-cheat: the server only stores what the client sends and merges by
 * `Math.max` per field, so missing a day can't lower a recorded best — but
 * a determined client could submit fake highs *under their own account*.
 * Cross-account forgery is prevented by the token check on the server.
 */

import { getOrCreateUserToken } from './coop.svelte';
import { whoami } from './directory';
import { readSettings } from './settings';
import { readAllGameStreaks, readProgressHistory, readProgressSnapshot, type WordBuildProgress, type WordSpeedProgress, type WordTowerProgress } from './progress';
import { getDayKey } from './daily';

export type LeaderScores = {
	'word-guess'?: number;
	'word-build'?: number;
	'word-group'?: number;
	'word-cross'?: number;
	'word-cross-m'?: number;
	'word-find'?: number;
	'word-crypto'?: number;
	'word-speed'?: number;
	'num-fill'?: number;
	'word-tower'?: number;
};

export type LeaderRecord = {
	userId: string;
	username: string;
	avatar: string;
	scores: LeaderScores;
	updatedAt: number;
	featuredBadge?: string;
};

export type GameKey = 'word-guess' | 'word-build' | 'word-group' | 'word-cross' | 'word-cross-m' | 'word-find' | 'word-crypto' | 'word-speed' | 'num-fill' | 'word-tower';

/** Raw per-game facts submitted for a day. The server validates + scores. */
export type DailyRaw = {
	'word-guess'?: { solved: boolean; guesses: number; coop?: boolean };
	'word-group'?: { solvedGroups: number; totalGroups: number; mistakesRemaining: number; coop?: boolean };
	'word-crypto'?: { solved: boolean; mistakes: number; coop?: boolean };
	'word-cross'?: { complete: boolean; revealed: boolean; filledCells: number; totalCells: number; checksUsed: number; coop?: boolean };
	'word-cross-m'?: { complete: boolean; revealed: boolean; filledCells: number; totalCells: number; checksUsed: number; coop?: boolean };
	'word-find'?: { complete: boolean; foundWords: number; totalWords: number; coop?: boolean };
	'word-build'?: { score: number; maxScore: number; foundWords: number; totalWords: number; coop?: boolean };
	'word-speed'?: { score: number; words: number; wpm: number; accuracy: number; coop?: boolean };
	'num-fill'?: { solved: boolean; mistakes: number; hintsUsed: number; coop?: boolean };
	'word-tower'?: { score: number; words: number; coop?: boolean };
};

export type DailyBoardRow = {
	userId: string;
	username: string;
	avatar: string;
	pts: number;
	solved: boolean;
	mode?: 'Solo' | 'Co-op';
	/** Per-game performance label, e.g. "3/6", "2 mistakes" (specific game only). */
	stat?: string;
	/** Last time this user submitted a daily result for this day (ms epoch). */
	updatedAt?: number;
	featuredBadge?: string;
};

export type DailyBoardResult = {
	rows: DailyBoardRow[];
	extra: DailyBoardRow[];
	total: number;
};

let publishTimer: ReturnType<typeof setTimeout> | null = null;

function leadersUrl(): string {
	const host = (import.meta.env.VITE_PARTYKIT_HOST as string | undefined) ?? (import.meta.env.DEV ? 'localhost:1999' : 'bubble-games-coop.loljoshie.partykit.dev');
	const base = host.startsWith('localhost') ? `http://${host}` : `https://${host}`;
	return `${base}/parties/leaders/global`;
}

async function leadersPost<T>(body: Record<string, unknown>): Promise<T | null> {
	try {
		const res = await fetch(leadersUrl(), {
			method: 'POST',
			headers: { 'content-type': 'application/json' },
			body: JSON.stringify(body)
		});
		if (!res.ok) return null;
		return (await res.json()) as T;
	} catch {
		return null;
	}
}

export async function submitLeaderScores(userId: string, username: string, avatar: string, scores: LeaderScores, featuredBadge = ''): Promise<LeaderRecord | null> {
	const res = await leadersPost<{ ok: boolean; record?: LeaderRecord }>({
		type: 'submit',
		userId,
		username,
		avatar,
		scores,
		featuredBadge,
		token: getOrCreateUserToken()
	});
	return res?.record ?? null;
}

export function computeLeaderScores(): LeaderScores {
	const streaks = readAllGameStreaks();
	const history = readProgressHistory();
	let wordBuildBest = 0;
	let wordSpeedBest = 0;
	let wordTowerBest = 0;
	for (const day of Object.values(history)) {
		const build = (day as { 'word-build'?: WordBuildProgress })['word-build'];
		if (build && typeof build.score === 'number' && build.score > wordBuildBest) {
			wordBuildBest = build.score;
		}
		const speed = (day as { 'word-speed'?: WordSpeedProgress })['word-speed'];
		if (speed && typeof speed.score === 'number' && speed.score > wordSpeedBest) {
			wordSpeedBest = speed.score;
		}
		const tower = (day as { 'word-tower'?: WordTowerProgress })['word-tower'];
		if (tower && typeof tower.score === 'number' && tower.score > wordTowerBest) {
			wordTowerBest = tower.score;
		}
	}
	return {
		'word-guess': streaks['word-guess'].best,
		'word-build': wordBuildBest,
		'word-group': streaks['word-group'].best,
		'word-cross': streaks['word-cross'].best,
		'word-cross-m': streaks['word-cross-m'].best,
		'word-find': streaks['word-find'].best,
		'word-crypto': streaks['word-crypto'].best,
		'word-speed': wordSpeedBest,
		'num-fill': streaks['num-fill'].best,
		'word-tower': wordTowerBest
	};
}

/** Today's per-game facts, only for games actually finished/played today. */
export function computeDailyRaw(): DailyRaw {
	const snap = readProgressSnapshot();
	const out: DailyRaw = {};

	const wg = snap['word-guess'];
	if (wg && wg.complete) out['word-guess'] = { solved: !!wg.solved, guesses: wg.guessesUsed, coop: !!wg.coop };

	const grp = snap['word-group'];
	if (grp && grp.complete)
		out['word-group'] = {
			solvedGroups: grp.solvedGroups,
			totalGroups: grp.totalGroups,
			mistakesRemaining: grp.mistakesRemaining,
			coop: !!grp.coop
		};

	const cry = snap['word-crypto'];
	if (cry && cry.complete) out['word-crypto'] = { solved: !!cry.solved, mistakes: cry.mistakes, coop: !!cry.coop };

	const cross = snap['word-cross'];
	if (cross && cross.complete)
		out['word-cross'] = {
			complete: !!cross.complete,
			revealed: !!cross.revealed,
			filledCells: cross.filledCells,
			totalCells: cross.totalCells,
			checksUsed: cross.checksUsed ?? 0,
			coop: !!cross.coop
		};

	const crossM = snap['word-cross-m'];
	if (crossM && crossM.complete)
		out['word-cross-m'] = {
			complete: !!crossM.complete,
			revealed: !!crossM.revealed,
			filledCells: crossM.filledCells,
			totalCells: crossM.totalCells,
			checksUsed: crossM.checksUsed ?? 0,
			coop: !!crossM.coop
		};

	const find = snap['word-find'];
	if (find && find.complete) out['word-find'] = { complete: !!find.complete, foundWords: find.foundWords, totalWords: find.totalWords, coop: !!find.coop };

	const build = snap['word-build'];
	if (build && build.maxScore > 0 && build.score > 0) out['word-build'] = { score: build.score, maxScore: build.maxScore, foundWords: build.foundWords, totalWords: build.totalWords, coop: !!build.coop };

	const speed = snap['word-speed'];
	if (speed && speed.complete) out['word-speed'] = { score: speed.score, words: speed.words, wpm: speed.wpm, accuracy: speed.accuracy, coop: !!speed.coop };

	const fill = snap['num-fill'];
	if (fill && fill.complete) out['num-fill'] = { solved: !!fill.solved, mistakes: fill.mistakes, hintsUsed: fill.hintsUsed, coop: !!fill.coop };

	const tower = snap['word-tower'];
	if (tower && tower.complete && tower.score > 0) out['word-tower'] = { score: tower.score, words: tower.words, coop: !!tower.coop };

	return out;
}

export async function submitDailyResults(userId: string, username: string, avatar: string, games: DailyRaw, day = getDayKey(), featuredBadge = ''): Promise<boolean> {
	if (Object.keys(games).length === 0) {
		return false;
	}
	const res = await leadersPost<{ ok: boolean }>({
		type: 'submit-daily',
		day,
		userId,
		username,
		avatar,
		games,
		featuredBadge,
		token: getOrCreateUserToken()
	});
	return !!res?.ok;
}

export async function fetchDailyBoard(day: string, game: GameKey | 'combined', userIds: string[] = []): Promise<DailyBoardResult> {
	const res = await leadersPost<DailyBoardResult>({ type: 'fetch-daily', day, game, userIds });
	return res ?? { rows: [], extra: [], total: 0 };
}

/**
 * Wipe this user's leaderboard records (all-time + every daily) on the server.
 * Called when the user resets game data or deletes their account so a reset is
 * a true full wipe — and so resetting can't be used to farm the daily board.
 */
export async function deleteMyLeaderData(userId: string): Promise<boolean> {
	const res = await leadersPost<{ ok: boolean }>({
		type: 'delete',
		userId,
		token: getOrCreateUserToken()
	});
	return !!res?.ok;
}

export async function publishMyLeaderScores(userId: string): Promise<LeaderRecord | null> {
	const username = await whoami(userId);
	if (!username) {
		return null;
	}
	const settings = readSettings();
	const avatar = settings.avatar || '';
	const featuredBadge = settings.featuredBadge || '';
	// Post today's daily results alongside the all-time bests, in parallel.
	// Both are awaited so a caller that fetches the board right after publish
	// (e.g. the leaders page on mount) sees the just-submitted score. A failure
	// of one must not reject the other.
	const [, record] = await Promise.all([submitDailyResults(userId, username, avatar, computeDailyRaw(), getDayKey(), featuredBadge).catch(() => false), submitLeaderScores(userId, username, avatar, computeLeaderScores(), featuredBadge).catch(() => null)]);
	return record;
}

export function scheduleLeaderScorePublish(userId: string, delay = 1000) {
	if (publishTimer) clearTimeout(publishTimer);
	publishTimer = setTimeout(() => {
		publishTimer = null;
		void publishMyLeaderScores(userId);
	}, delay);
}

export async function fetchLeaderRecords(userIds: string[], requesterUserId?: string): Promise<LeaderRecord[]> {
	if (userIds.length === 0) return [];
	const res = await leadersPost<{ records: LeaderRecord[] }>({ type: 'fetch', userIds, userId: requesterUserId });
	return res?.records ?? [];
}

export type GlobalLeaderResult = {
	records: LeaderRecord[];
	extra: LeaderRecord[];
	total: number;
};

/** Fetch the GLOBAL all-time top-N for a given game (or 'overall'). The
 *  `alwaysIncludeUserIds` list (self + friends) is returned as `extra` if any
 *  of them are ranked below the top-N cutoff, so the requester still sees
 *  their own and their friends' rows. */
export async function fetchGlobalLeaderRecords(game: GameKey | 'overall', alwaysIncludeUserIds: string[] = []): Promise<GlobalLeaderResult> {
	const res = await leadersPost<GlobalLeaderResult>({
		type: 'fetch-global',
		game,
		userIds: alwaysIncludeUserIds
	});
	return res ?? { records: [], extra: [], total: 0 };
}
